import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { AlertService } from 'src/app/services/auth';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import {
  OFFLINE_OP_STATUS_LABELS,
  PendingSubmissionRow,
} from 'src/app/services/offline/pending-submission.model';
import { SyncService } from 'src/app/services/offline/sync.service';
import {
  HormigonPendingOperationEditDialogComponent,
  PendingOperationEditDialogData,
} from './pending-operation-edit-dialog.component';

@Component({
    selector: 'app-hormigon-envios-pendientes-list',
    templateUrl: './envios-pendientes-list.component.html',
    styleUrls: ['./envios-pendientes-list.component.css'],
    standalone: false
})
export class HormigonEnviosPendientesListComponent implements OnInit, OnDestroy {
  rows: PendingSubmissionRow[] = [];
  isOnline = true;
  isFlushing = false;
  isRetrying = false;
  readonly statusLabels = OFFLINE_OP_STATUS_LABELS;
  readonly actionInProgress = new Set<string>();

  private subs: Subscription[] = [];

  constructor(
    private offlineStorage: OfflineStorageService,
    private syncService: SyncService,
    private alertService: AlertService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.isOnline = this.offlineStorage.isOnline();
    this.subs.push(
      this.offlineStorage.online$.subscribe((online) => {
        this.isOnline = online;
      }),
      this.syncService.flushing$.subscribe((flushing) => {
        this.isFlushing = flushing;
        if (!flushing && this.isRetrying) {
          this.isRetrying = false;
          this.loadRows();
        }
      }),
      this.offlineStorage.queueChanged$.subscribe(() => this.loadRows()),
      this.offlineStorage.operationQueued$.subscribe(() => this.loadRows()),
    );
    this.loadRows();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
  }

  async loadRows(): Promise<void> {
    this.rows = await this.offlineStorage.getPendingSubmissions();
  }

  async reenviarPendientes(): Promise<void> {
    if (!this.isOnline) {
      this.alertService.error(
        'Sin conexión — los envíos se reintentarán automáticamente al recuperar internet.'
      );
      return;
    }
    if (!this.hasRetryableRows) {
      return;
    }
    this.isRetrying = true;
    await this.syncService.flushNowAndWait();
    await this.loadRows();
    this.isRetrying = false;
  }

  statusClass(status: PendingSubmissionRow['status']): string {
    switch (status) {
      case 'syncing':
        return 'badge-syncing';
      case 'failed':
        return 'badge-failed';
      case 'blocked':
        return 'badge-blocked';
      case 'paused':
        return 'badge-paused';
      default:
        return 'badge-pending';
    }
  }

  truncateError(message?: string, max = 80): string {
    if (!message) {
      return '—';
    }
    return message.length > max ? `${message.slice(0, max)}…` : message;
  }

  get hasRetryableRows(): boolean {
    return this.rows.some((row) => row.status !== 'paused' && row.status !== 'syncing');
  }

  isActionBusy(row: PendingSubmissionRow): boolean {
    return this.actionInProgress.has(row.operationId);
  }

  isHeld(row: PendingSubmissionRow): boolean {
    return row.status === 'paused' || row.status === 'blocked';
  }

  canEdit(row: PendingSubmissionRow): boolean {
    const editable = row.operationType === 'signedCanvas' ||
      row.operationType === 'signed' ||
      row.operationType === 'extraValues';
    return this.isHeld(row) && editable && this.belongsToCurrentUser(row);
  }

  async togglePaused(row: PendingSubmissionRow): Promise<void> {
    if (row.status === 'syncing' || this.isActionBusy(row)) return;
    this.actionInProgress.add(row.operationId);
    try {
      const changed = this.isHeld(row)
        ? await this.offlineStorage.resumeOperation(row.tkt, row.operationId)
        : await this.offlineStorage.pauseOperation(row.tkt, row.operationId);
      if (!changed) {
        this.alertService.error('El envío cambió de estado. Actualiza la lista e intenta nuevamente.');
        return;
      }
      if (this.isHeld(row) && this.offlineStorage.isOnline()) {
        this.syncService.flushNow();
      }
    } catch (error) {
      this.alertService.error(
        error instanceof Error ? error.message : 'No se pudo actualizar el estado del envío.'
      );
    } finally {
      this.actionInProgress.delete(row.operationId);
      await this.loadRows();
    }
  }

  async openEditModal(row: PendingSubmissionRow): Promise<void> {
    if (!this.canEdit(row) || this.isActionBusy(row)) return;
    this.actionInProgress.add(row.operationId);
    try {
      const operation = await this.offlineStorage.getOperation(row.tkt, row.operationId);
      if (!operation || (operation.status !== 'paused' && operation.status !== 'blocked')) {
        this.alertService.error('La operación ya no está pausada o dejó de existir.');
        await this.loadRows();
        return;
      }
      const data: PendingOperationEditDialogData = {
        tkt: row.tkt,
        operationId: row.operationId,
        operation,
      };
      const ref = this.dialog.open(HormigonPendingOperationEditDialogComponent, {
        data,
        maxWidth: '96vw',
        disableClose: true,
      });
      ref.afterClosed().subscribe(async (saved) => {
        if (saved) {
          this.alertService.success('Los cambios se guardaron localmente. El envío continúa pausado.');
        }
        await this.loadRows();
      });
    } catch (error) {
      this.alertService.error(
        error instanceof Error ? error.message : 'No se pudo abrir la edición del envío.'
      );
    } finally {
      this.actionInProgress.delete(row.operationId);
    }
  }

  private belongsToCurrentUser(row: PendingSubmissionRow): boolean {
    if (!row.userRut) return true;
    try {
      const user = JSON.parse(localStorage.getItem('user') ?? 'null');
      const currentRut = user?.RUT_USUARIO == null ? '' : String(user.RUT_USUARIO).trim();
      return !!currentRut && currentRut === String(row.userRut).trim();
    } catch {
      return false;
    }
  }
}
